<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import PopupForm from './weight/WeightForm.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSquarePlus, faRotate } from '@fortawesome/pro-light-svg-icons';

  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let weights = [];
  let compliancesCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let showForm = false;
  let showPopup = false;
  let selectedWeight = null;
  let scheduleItems = [];
  let selectedScheduleId = null;

  // BMI Visualization Variables
  let bmi = null;
  let fmi = null;
  let ffmi = null;
  let smm = null;
  let tbw = null;
  let ecw = null;

  let currentBMICategory = null;
  let bmiMarkerPosition = 0;
  let currentFMICategory = null;
  let fmiMarkerPosition = 0;
  let currentFFMICategory = null;
  let ffmiMarkerPosition = 0;
  let currentSMMCategory = null;
  let smmMarkerPosition = 0;
  let currentTBWCategory = null;
  let tbwMarkerPosition = 0;
  let currentECWCategory = null;
  let ecwMarkerPosition = 0;

  let bmiCategories = null;
  let fmiCategories = null;
  let ffmiCategories = null;
  let smmCategories = null;
  let tbwCategories = null;
  let ecwCategories = null;

  const colorMapping = {
    Warning: '#FFD700', // Gold for Warning
    Normal: '#32CD32', // LimeGreen for Normal
    Danger: '#FF4500', // OrangeRed for Danger
    Neutral: '#AAAAAA', // Gray for Neutral
    // Add other normTypes if necessary
  };

  $: if (selectedWeight?.biaCalculations) {
    const updateCategoryArray = (normValues) => {
      return normValues.map((nv) => ({
        name: nv.label || nv.normType,
        range: [nv.start.value, nv.end.value],
        color: colorMapping[nv.normType] || '#000000',
      }));
    };

    bmiCategories = updateCategoryArray(selectedWeight.biaCalculations.bmi.normValues);
    fmiCategories = updateCategoryArray(selectedWeight.biaCalculations.fmi.normValues);
    smmCategories = updateCategoryArray(selectedWeight.biaCalculations.smm.normValues);
    tbwCategories = updateCategoryArray(selectedWeight.biaCalculations.tbw.normValues);
    ecwCategories = updateCategoryArray(selectedWeight.biaCalculations.ecw.normValues);

    // Assign the values from selectedWeight
    bmi = selectedWeight.biaCalculations.bmi.value;
    fmi = selectedWeight.biaCalculations.fmi.value;
    smm = selectedWeight.biaCalculations.smm.value;
    tbw = selectedWeight.biaCalculations.tbw.value;
    ecw = selectedWeight.biaCalculations.ecw.value;

    if (tbw > 39) {
      tbw = 35;
    }

    // Find the appropriate category for the current BMI, FMI, etc.
    currentBMICategory = bmiCategories.find((cat) => bmi >= cat.range[0] && bmi <= cat.range[1]);
    currentFMICategory = fmiCategories.find((cat) => fmi >= cat.range[0] && fmi <= cat.range[1]);
    currentSMMCategory = smmCategories.find((cat) => smm >= cat.range[0] && smm <= cat.range[1]);
    currentTBWCategory = tbwCategories.find((cat) => tbw >= cat.range[0] && tbw <= cat.range[1]);
    currentECWCategory = ecwCategories.find((cat) => ecw >= cat.range[0] && ecw <= cat.range[1]);

    // Calculate marker positions
    bmiMarkerPosition = ((bmi - 10) / (50 - 10)) * 100;
    fmiMarkerPosition = ((fmi - 0) / (20 - 0)) * 100;
    smmMarkerPosition = ((smm - 0) / (30 - 0)) * 100;
    tbwMarkerPosition = ((tbw - 29) / (39 - 29)) * 100;
    ecwMarkerPosition = ((ecw - 13.5) / (18 - 13.5)) * 100;
  }

  async function fetchScheduleItems(subjectGUID) {
    try {
      const res = await fetch(`${API_BASE_URL}/schedule?subjectGUID=${subjectGUID}`);

      if (!res.ok) {
        throw new Error('Failed to fetch schedule items');
      }

      const data = await res.json();
      scheduleItems = data || [];

      // Find the first item with statusLabel "Started"
      const startedItem = scheduleItems.find((item) => item.statusLabel === 'Started');

      // Set the selectedScheduleId to the _id of the found item, or null if none exists
      selectedScheduleId = startedItem ? startedItem._id : null;

      //console.info("startedItem.name: ", startedItem.name);
      //console.info("selectedScheduleId: ", selectedScheduleId);

      isLoading = false;
    } catch (err) {
      scheduleItems = [];
      console.error('Error fetching schedule items:', err);
      isLoading = false;
    }
  }

  async function fetchWeights(page = 1) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/weight?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch weight');
      }

      const data = await res.json();

      weights = data.data || [];
      compliancesCount = data.totalCount || 0;
      currentPage = data.page || 1;
      totalPages = data.totalPages || 1;
    } catch (err) {
      console.error('Error fetching weights:', err);
      weights = []; // Set to empty array in case of error
    }
  }

  onMount(() => {
    if ($selectedParticipant) {
      fetchWeights(1);
      fetchScheduleItems($selectedParticipant._id);
    }
  });

  async function handleExportCSV() {}

  async function handleExportExcel() {}

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchWeights(newPage);
    }
  }

  function handleRefresh() {
    //console.log("handleRefresh");
    fetchWeights(1);
  }

  function handleNewClick() {
    showForm = true;
  }

  function handleRowClick(weight) {
    selectedWeight = JSON.parse(weight.raw); // Parse the raw JSON data
    showPopup = true;
  }

  function closePopup() {
    showPopup = false;
    selectedWeight = null;
  }

  async function handleCreateWeight(event) {
    const newWeight = event.detail;

    // Add the read, received, and subjectGUID fields
    const currentTimestamp = new Date().toISOString();

    newWeight.read = currentTimestamp;
    newWeight.received = currentTimestamp;
    newWeight.subjectGUID = $selectedParticipant._id;

    const rawData = {
      tenantId: 'pdhdev',
      userId: 'john_doe',
      id: '3a274b9d-2fdc-4cb1-b099-2f0742c9ca71',
      measurementDate: '1975-06-30',
      user: {
        firstName: 'John',
        lastName: 'Doe',
        gender: 'Male',
        ethnicity: 'Caucasian',
        birthDate: '1975-06-30',
      },
      biaCalculations: {
        bmi: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Warning',
              label: 'Underweight',
              start: {
                value: 14,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 18.5,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'NormalWeight',
              start: {
                value: 18.5,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 25,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: 'Overweight',
              start: {
                value: 25,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 30,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'Obesity',
              start: {
                value: 30,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 45,
                unit: 'KgM2',
                percentile: null,
              },
            },
          ],
          value: 22.5,
          unit: 'KgM2',
        },
        weight: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Warning',
              label: 'Underweight',
              start: {
                value: 46.37,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 61.28,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'NormalWeight',
              start: {
                value: 61.28,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 82.81,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: 'Overweight',
              start: {
                value: 82.81,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 99.37,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'Obesity',
              start: {
                value: 99.37,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 149.06,
                unit: 'Kg',
                percentile: null,
              },
            },
          ],
          value: 74.5,
          unit: 'Kg',
        },
        height: {
          trend: null,
          change: null,
          normValues: null,
          value: 182,
          unit: 'Cm',
        },
        wc: null,
        tbw: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Neutral',
              label: 'Low',
              start: {
                value: 38.3,
                unit: 'L',
                percentile: 0.1,
              },
              end: {
                value: 41.3,
                unit: 'L',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 41.3,
                unit: 'L',
                percentile: 5,
              },
              end: {
                value: 44.8,
                unit: 'L',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 44.8,
                unit: 'L',
                percentile: 50,
              },
              end: {
                value: 48.2,
                unit: 'L',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Neutral',
              label: 'High',
              start: {
                value: 48.2,
                unit: 'L',
                percentile: 95,
              },
              end: {
                value: 51.3,
                unit: 'L',
                percentile: 99.9,
              },
            },
          ],
          value: 38.9,
          unit: 'L',
        },
        tbwp: {
          trend: null,
          change: null,
          normValues: null,
          value: 51.9,
          unit: 'Percent',
        },
        ecw: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Neutral',
              label: null,
              start: {
                value: 15.9,
                unit: 'L',
                percentile: 0.1,
              },
              end: {
                value: 17.2,
                unit: 'L',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 17.2,
                unit: 'L',
                percentile: 5,
              },
              end: {
                value: 18.7,
                unit: 'L',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 18.7,
                unit: 'L',
                percentile: 50,
              },
              end: {
                value: 20.2,
                unit: 'L',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Neutral',
              label: null,
              start: {
                value: 20.2,
                unit: 'L',
                percentile: 95,
              },
              end: {
                value: 21.5,
                unit: 'L',
                percentile: 99.9,
              },
            },
          ],
          value: 16.5,
          unit: 'L',
        },
        ecwp: {
          trend: null,
          change: null,
          normValues: null,
          value: 22,
          unit: 'Percent',
        },
        ecwByTbw: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Neutral',
              label: 'Low',
              start: {
                value: 38,
                unit: 'Percent',
                percentile: 0.1,
              },
              end: {
                value: 39.5,
                unit: 'Percent',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 39.5,
                unit: 'Percent',
                percentile: 5,
              },
              end: {
                value: 41.2,
                unit: 'Percent',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 41.2,
                unit: 'Percent',
                percentile: 50,
              },
              end: {
                value: 43,
                unit: 'Percent',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Neutral',
              label: 'High',
              start: {
                value: 43,
                unit: 'Percent',
                percentile: 95,
              },
              end: {
                value: 44.5,
                unit: 'Percent',
                percentile: 99.9,
              },
            },
          ],
          value: 42.4,
          unit: 'Percent',
        },
        fm: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Normal',
              label: 'Low',
              start: {
                value: 0,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 4.75,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 4.75,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 18.97,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: 'Increased',
              start: {
                value: 18.97,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 29.9,
                unit: 'Kg',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'High',
              start: {
                value: 29.9,
                unit: 'Kg',
                percentile: null,
              },
              end: {
                value: 62.7,
                unit: 'Kg',
                percentile: null,
              },
            },
          ],
          value: 21.45,
          unit: 'Kg',
        },
        fmp: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Normal',
              label: 'Low',
              start: {
                value: 0,
                unit: 'Percent',
                percentile: null,
              },
              end: {
                value: 7.8,
                unit: 'Percent',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 7.8,
                unit: 'Percent',
                percentile: null,
              },
              end: {
                value: 22.9,
                unit: 'Percent',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: 'Increased',
              start: {
                value: 22.9,
                unit: 'Percent',
                percentile: null,
              },
              end: {
                value: 30.1,
                unit: 'Percent',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'High',
              start: {
                value: 30.1,
                unit: 'Percent',
                percentile: null,
              },
              end: {
                value: 42.1,
                unit: 'Percent',
                percentile: null,
              },
            },
          ],
          value: 28.8,
          unit: 'Percent',
        },
        fmi: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Normal',
              label: 'Low',
              start: {
                value: 0,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 1.4,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: 1.4,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 5.7,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: 'Increased',
              start: {
                value: 5.7,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 9,
                unit: 'KgM2',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'High',
              start: {
                value: 9,
                unit: 'KgM2',
                percentile: null,
              },
              end: {
                value: 18.9,
                unit: 'KgM2',
                percentile: null,
              },
            },
          ],
          value: 6.5,
          unit: 'KgM2',
        },
        zfmi: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Normal',
              label: 'LowFat',
              start: {
                value: -3.5,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: -1.6,
                unit: 'None',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: -1.6,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: 1.6,
                unit: 'None',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Danger',
              label: 'HighFat',
              start: {
                value: 1.6,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: 3.5,
                unit: 'None',
                percentile: null,
              },
            },
          ],
          value: 0,
          unit: 'None',
        },
        smm: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.31,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 26.89,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Warning',
              label: null,
              start: {
                value: 26.89,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 29.82,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 29.82,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 32.75,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.75,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 35.33,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 25.55,
          unit: 'Kg',
        },
        smmp: {
          trend: null,
          change: null,
          normValues: null,
          value: 34.3,
          unit: 'Percent',
        },
        smmOverAge: {
          currentAgeNormValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.31,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 26.89,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 26.89,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 29.82,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 29.82,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 32.75,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.75,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 35.33,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          projection: [
            {
              age: 18,
              value: 26.068196148549568,
              unit: 'Kg',
            },
            {
              age: 32,
              value: 26.361251795069567,
              unit: 'Kg',
            },
            {
              age: 49,
              value: 25.55205270389637,
              unit: 'Kg',
            },
            {
              age: 65,
              value: 23.623190457457966,
              unit: 'Kg',
            },
          ],
          percentile: 1,
          trend: null,
          change: null,
          normValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.309935893122162,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 26.887186577762794,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 26.887186577762794,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 29.82012252782837,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 29.82012252782837,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 32.753058477893944,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.753058477893944,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 35.33030916253458,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 18,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.826079337775365,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 27.403330022415997,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 27.403330022415997,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 30.33626597248157,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 30.33626597248157,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 33.26920192254715,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 33.26920192254715,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 35.84645260718778,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 32,
              normType: 'Danger',
              label: null,
              start: {
                value: 25.119134984295364,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 27.696385668935996,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 27.696385668935996,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 30.629321619001573,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 30.629321619001573,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 33.56225756906715,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 33.56225756906715,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 36.13950825370778,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 65,
              normType: 'Danger',
              label: null,
              start: {
                value: 22.381073646683763,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 24.958324331324395,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 24.958324331324395,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 27.89126028138997,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 27.89126028138997,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 30.82419623145555,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 30.82419623145555,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 33.40144691609618,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 25.55,
          unit: 'Kg',
        },
        smmOverAgeBMIIndependant: {
          currentAgeNormValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.32,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 28.23,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 28.23,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 32.67,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.67,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 37.11,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 37.11,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 41.02,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          projection: [
            {
              age: 18,
              value: 24.115519511620366,
              unit: 'Kg',
            },
            {
              age: 32,
              value: 25.618005602180364,
              unit: 'Kg',
            },
            {
              age: 49,
              value: 25.552052703896365,
              unit: 'Kg',
            },
            {
              age: 65,
              value: 23.595991996568365,
              unit: 'Kg',
            },
          ],
          percentile: 1,
          trend: null,
          change: null,
          normValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.320959403769393,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 28.226190078847125,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 28.226190078847125,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 32.670379970228,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.670379970228,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 37.11456986160888,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 37.11456986160888,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 41.01980053668661,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 18,
              normType: 'Danger',
              label: null,
              start: {
                value: 22.88442621149339,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 26.789656886571123,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 26.789656886571123,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 31.233846777952,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 31.233846777952,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 35.678036669332876,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 35.678036669332876,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 39.58326734441061,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 32,
              normType: 'Danger',
              label: null,
              start: {
                value: 24.386912302053393,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 28.292142977131125,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 28.292142977131125,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 32.736332868512,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 32.736332868512,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 37.180522759892874,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 37.180522759892874,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 41.085753434970606,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
            {
              age: 65,
              normType: 'Danger',
              label: null,
              start: {
                value: 22.364898696441394,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 26.270129371519126,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 26.270129371519126,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 30.7143192629,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 30.7143192629,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 35.15850915428088,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 35.15850915428088,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 39.06373982935861,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 25.55,
          unit: 'Kg',
        },
        zsmi: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: 'LowMuscle',
              start: {
                value: -3.5,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: -1.6,
                unit: 'None',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'Normal',
              start: {
                value: -1.6,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: 1.6,
                unit: 'None',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: 'HighMuscle',
              start: {
                value: 1.6,
                unit: 'None',
                percentile: null,
              },
              end: {
                value: 3.5,
                unit: 'None',
                percentile: null,
              },
            },
          ],
          value: -2.6,
          unit: 'None',
        },
        ssmmRightArm: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 1.32,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 1.61,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.61,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 1.95,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.95,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 2.28,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.28,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 2.57,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 1.37,
          unit: 'Kg',
        },
        ssmmLeftArm: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 1.25,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 1.54,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.54,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 1.86,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.86,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 2.18,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.18,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 2.47,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 1.36,
          unit: 'Kg',
        },
        ssmmRightLeg: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.38,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 5.17,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.17,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 6.06,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.06,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 6.95,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.95,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 7.73,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 5.52,
          unit: 'Kg',
        },
        ssmmLeftLeg: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.33,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 5.11,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.11,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 6.01,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.01,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 6.91,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.91,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 7.7,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 5.69,
          unit: 'Kg',
        },
        ssmmTorso: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 10.91,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 12.33,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 12.33,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 13.95,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 13.95,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 15.56,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 15.56,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 16.98,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 11.61,
          unit: 'Kg',
        },
        ssmmRightArmBMIIndependant: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 1.39,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 1.73,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.73,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 2.13,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.13,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 2.52,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.52,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 2.87,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 1.37,
          unit: 'Kg',
        },
        ssmmLeftArmBMIIndependant: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 1.32,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 1.65,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 1.65,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 2.03,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.03,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 2.41,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 2.41,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 2.75,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 1.36,
          unit: 'Kg',
        },
        ssmmRightLegBMIIndependant: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.5,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 5.53,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.53,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 6.71,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.71,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 7.88,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 7.88,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 8.91,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 5.52,
          unit: 'Kg',
        },
        ssmmLeftLegBMIIndependant: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.46,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 5.48,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.48,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 6.65,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.65,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 7.81,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 7.81,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 8.84,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 5.69,
          unit: 'Kg',
        },
        ssmmTorsoBMIIndependant: {
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Danger',
              label: null,
              start: {
                value: 11.12,
                unit: 'Kg',
                percentile: 0.1,
              },
              end: {
                value: 13.01,
                unit: 'Kg',
                percentile: 5,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 13.01,
                unit: 'Kg',
                percentile: 5,
              },
              end: {
                value: 15.16,
                unit: 'Kg',
                percentile: 50,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 15.16,
                unit: 'Kg',
                percentile: 50,
              },
              end: {
                value: 17.31,
                unit: 'Kg',
                percentile: 95,
              },
            },
            {
              age: null,
              normType: 'Normal',
              label: null,
              start: {
                value: 17.31,
                unit: 'Kg',
                percentile: 95,
              },
              end: {
                value: 19.19,
                unit: 'Kg',
                percentile: 99.9,
              },
            },
          ],
          value: 11.61,
          unit: 'Kg',
        },
        vat: null,
        pha: {
          currentAgeNormValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.4,
                unit: 'Degree',
                percentile: 0.1,
              },
              end: {
                value: 5,
                unit: 'Degree',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 5,
                unit: 'Degree',
                percentile: 5,
              },
              end: {
                value: 5.8,
                unit: 'Degree',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.8,
                unit: 'Degree',
                percentile: 50,
              },
              end: {
                value: 6.5,
                unit: 'Degree',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.5,
                unit: 'Degree',
                percentile: 95,
              },
              end: {
                value: 7.2,
                unit: 'Degree',
                percentile: 99.9,
              },
            },
          ],
          projection: [
            {
              age: 18,
              value: 5.30636398526792,
              unit: 'Degree',
            },
            {
              age: 32,
              value: 5.35546758526792,
              unit: 'Degree',
            },
            {
              age: 65,
              value: 4.42329104326792,
              unit: 'Degree',
            },
            {
              age: 49,
              value: 5.0590300512679205,
              unit: 'Degree',
            },
          ],
          percentile: 6,
          trend: null,
          change: null,
          normValues: [
            {
              age: 49,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.367649448416,
                unit: 'Degree',
                percentile: 0.1,
              },
              end: {
                value: 5.026975967652,
                unit: 'Degree',
                percentile: 5,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.026975967652,
                unit: 'Degree',
                percentile: 5,
              },
              end: {
                value: 5.777295858,
                unit: 'Degree',
                percentile: 50,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.777295858,
                unit: 'Degree',
                percentile: 50,
              },
              end: {
                value: 6.527615748348,
                unit: 'Degree',
                percentile: 95,
              },
            },
            {
              age: 49,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.527615748348,
                unit: 'Degree',
                percentile: 95,
              },
              end: {
                value: 7.186942267584,
                unit: 'Degree',
                percentile: 99.9,
              },
            },
            {
              age: 18,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.614983382416,
                unit: 'Degree',
                percentile: 0.1,
              },
              end: {
                value: 5.274309901652,
                unit: 'Degree',
                percentile: 5,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.274309901652,
                unit: 'Degree',
                percentile: 5,
              },
              end: {
                value: 6.024629792,
                unit: 'Degree',
                percentile: 50,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.024629792,
                unit: 'Degree',
                percentile: 50,
              },
              end: {
                value: 6.774949682348,
                unit: 'Degree',
                percentile: 95,
              },
            },
            {
              age: 18,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.774949682348,
                unit: 'Degree',
                percentile: 95,
              },
              end: {
                value: 7.434276201584,
                unit: 'Degree',
                percentile: 99.9,
              },
            },
            {
              age: 32,
              normType: 'Danger',
              label: null,
              start: {
                value: 4.664086982416,
                unit: 'Degree',
                percentile: 0.1,
              },
              end: {
                value: 5.323413501652,
                unit: 'Degree',
                percentile: 5,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.323413501652,
                unit: 'Degree',
                percentile: 5,
              },
              end: {
                value: 6.073733392,
                unit: 'Degree',
                percentile: 50,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.073733392,
                unit: 'Degree',
                percentile: 50,
              },
              end: {
                value: 6.824053282348,
                unit: 'Degree',
                percentile: 95,
              },
            },
            {
              age: 32,
              normType: 'Normal',
              label: null,
              start: {
                value: 6.824053282348,
                unit: 'Degree',
                percentile: 95,
              },
              end: {
                value: 7.483379801584,
                unit: 'Degree',
                percentile: 99.9,
              },
            },
            {
              age: 65,
              normType: 'Danger',
              label: null,
              start: {
                value: 3.731910440416,
                unit: 'Degree',
                percentile: 0.1,
              },
              end: {
                value: 4.391236959652,
                unit: 'Degree',
                percentile: 5,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 4.391236959652,
                unit: 'Degree',
                percentile: 5,
              },
              end: {
                value: 5.14155685,
                unit: 'Degree',
                percentile: 50,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.14155685,
                unit: 'Degree',
                percentile: 50,
              },
              end: {
                value: 5.891876740348,
                unit: 'Degree',
                percentile: 95,
              },
            },
            {
              age: 65,
              normType: 'Normal',
              label: null,
              start: {
                value: 5.891876740348,
                unit: 'Degree',
                percentile: 95,
              },
              end: {
                value: 6.551203259584,
                unit: 'Degree',
                percentile: 99.9,
              },
            },
          ],
          value: 5.1,
          unit: 'Degree',
        },
        biva: {
          zRh: {
            value: 2.54704416989246,
            unit: 'None',
          },
          zXcH: {
            value: 1.11076215118044,
            unit: 'None',
          },
          normValues: [
            {
              percentile: 95,
              radiusX: 3.21306,
              radiusY: 1.31012,
              normType: 'Blank',
            },
            {
              percentile: 75,
              radiusX: 2.18362,
              radiusY: 0.890366,
              normType: 'Neutral',
            },
            {
              percentile: 50,
              radiusX: 1.54342,
              radiusY: 0.629323,
              normType: 'Normal',
            },
          ],
        },
        tbs: {
          topPercent: 98,
          fatScore: {
            trend: null,
            change: null,
            normValues: [
              {
                age: null,
                normType: 'Danger',
                label: null,
                start: {
                  value: 17,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 26,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Warning',
                label: null,
                start: {
                  value: 26,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 31,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Normal',
                label: null,
                start: {
                  value: 31,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 40,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Normal',
                label: null,
                start: {
                  value: 40,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 40,
                  unit: 'Point',
                  percentile: null,
                },
              },
            ],
            value: 30,
            unit: 'Point',
          },
          muscleScore: {
            trend: null,
            change: null,
            normValues: [
              {
                age: null,
                normType: 'Danger',
                label: null,
                start: {
                  value: 45,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 57,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Warning',
                label: null,
                start: {
                  value: 57,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 70,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Normal',
                label: null,
                start: {
                  value: 70,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 83,
                  unit: 'Point',
                  percentile: null,
                },
              },
              {
                age: null,
                normType: 'Normal',
                label: null,
                start: {
                  value: 83,
                  unit: 'Point',
                  percentile: null,
                },
                end: {
                  value: 95,
                  unit: 'Point',
                  percentile: null,
                },
              },
            ],
            value: 50,
            unit: 'Point',
          },
          trend: null,
          change: null,
          normValues: [
            {
              age: null,
              normType: 'Blank',
              label: 'KeepUp',
              start: {
                value: 70,
                unit: 'Point',
                percentile: null,
              },
              end: {
                value: 90,
                unit: 'Point',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Bronze',
              label: 'Bronze',
              start: {
                value: 90,
                unit: 'Point',
                percentile: null,
              },
              end: {
                value: 100,
                unit: 'Point',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Silver',
              label: 'Silver',
              start: {
                value: 100,
                unit: 'Point',
                percentile: null,
              },
              end: {
                value: 110,
                unit: 'Point',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Gold',
              label: 'Gold',
              start: {
                value: 110,
                unit: 'Point',
                percentile: null,
              },
              end: {
                value: 125,
                unit: 'Point',
                percentile: null,
              },
            },
            {
              age: null,
              normType: 'Platinum',
              label: 'Platinum',
              start: {
                value: 125,
                unit: 'Point',
                percentile: null,
              },
              end: {
                value: 130,
                unit: 'Point',
                percentile: null,
              },
            },
          ],
          value: 80,
          unit: 'Point',
        },
        xc: {
          trend: null,
          change: null,
          normValues: null,
          value: 62.5,
          unit: 'Ohm',
        },
        r: {
          trend: null,
          change: null,
          normValues: null,
          value: 706,
          unit: 'Ohm',
        },
      },
      createdAt: '2024-08-26T14:52:54.733-0700',
      updatedAt: '2024-08-26T14:52:54.733-0700',
    };

    newWeight.raw = JSON.stringify(rawData);

    try {
      const res = await fetch(`${API_BASE_URL}/weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newWeight),
      });

      if (!res.ok) {
        throw new Error('Failed to create weight');
      }

      await fetchWeights(1);
      showForm = false;
    } catch (err) {
      console.error('Error creating weight:', err);
    }
  }
</script>

<div class="flex justify-between ph-2 pb-0">
  <h2 class="text-s mb-4 text-sky-800 dark:text-white">
    {$selectedStudy ? 'Protocol: ' + $selectedStudy.protocolNumber + ' - ' : ''}{$selectedSite
      ? 'Site: ' + $selectedSite.siteName
      : ''}{$selectedParticipant
      ? ' - Subject: ' + $selectedParticipant.subjectId
      : ' - Weight Measurements'}
  </h2>
</div>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

{#if !isLoading && $selectedParticipant}
  <PopupForm
    {showForm}
    {scheduleItems}
    {selectedScheduleId}
    on:submit={handleCreateWeight}
    on:close={() => (showForm = false)}
  />
{/if}

<!-- Title and Buttons -->
<div class="flex justify-between pt-4 ph-2 pb-0 me-1">
  <h2 class="text-l mb-4 mx-4 text-sky-800 dark:text-white">Weight Measurements</h2>
  <div class="text-black dark:text-white">
    {compliancesCount} Items
    <button on:click={handleRefresh} class="pagination mx-1 p-0">
      <FontAwesomeIcon icon={faRotate} class="text-sky-800 dark:text-white" />
    </button>

    <button on:click={handleNewClick} class="pagination mx-1 p-0">
      <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white" />
    </button>
  </div>
</div>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold">Subject</th>
        <th class="px-4 py-2 text-left bold">Source</th>
        <th class="px-4 py-2 text-left bold">Serial Nr</th>
        <th class="px-4 py-2 text-left bold">Value</th>
        <th class="px-4 py-2 text-left bold">Unit</th>
        <th class="px-4 py-2 text-left bold">Timestamp</th>
        <th class="px-4 py-2 text-left bold">Timezone</th>
      </tr>
    </thead>
    <tbody>
      {#each weights as weight, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}
            >{$selectedParticipant.subjectId}</td
          >
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}
            >{weight.manufacturer} {weight.model}</td
          >
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}
            >{weight.externalId ? weight.externalId : ''}</td
          >
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}>{weight.value}</td>
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}>{weight.unit}</td>
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}
            >{weight.read
              ? formatToLocalDateTime(weight.read, $selectedParticipant.timezoneCode)
              : ''}</td
          >
          <td class="px-4 py-2" on:click={() => handleRowClick(weight)}>{weight.timezone}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<!-- Add Export Buttons -->
<div class="flex justify-between p-2 py-3">
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button hidden
      on:click={handleExportCSV}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >CSV</button
    >
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</div>

<!-- Popup for displaying raw data -->
{#if showPopup}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]">
    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-6xl h-4/5 z-[1001]">
      <header class="flex justify-between items-start mb-4">
        <h2 class="text-l text-gray-900 dark:text-gray-100">Measurement Details</h2>
        <button
          on:click={closePopup}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>

      <div class="space-y-2 text-gray-500 dark:text-slate-400">
        <p class="mb-3 text-right">
          {new Date(selectedWeight?.measurementDate).toLocaleDateString()}
          {new Date(selectedWeight?.measurementDate).toLocaleTimeString()}
        </p>

        <!-- BMI Visualization Component -->
        {#if bmi}
          <div class="bmi-container mx-0 my-2 text-s">
            <p>
              BMI
              {bmi} kg/m² Weight:
              {selectedWeight?.biaCalculations?.weight.value} kg Height:
              {selectedWeight?.biaCalculations?.height.value} cm - {currentBMICategory.name}
            </p>
            <div class="bmi-bar mt-1 opacity-80">
              {#each bmiCategories as category, index (index)}
                <div
                  class="category"
                  style="background-color: {category.color}; flex-grow: {category.range[1] -
                    category.range[0]};"
                ></div>
              {/each}
            </div>
            <div class="bmi-marker" style="--marker-position: {bmiMarkerPosition}%">⬤</div>
          </div>
        {/if}

        <!-- FMI Visualization Component -->
        {#if fmi}
          <div class="bmi-container mx-0 my-2 text-s">
            <p>
              Fat Mass Index:
              {fmi} kg/m² Fat Mass:
              {selectedWeight?.biaCalculations?.fm.value} kg ({selectedWeight?.biaCalculations?.fmp
                .value} %) - {currentFMICategory.name}
            </p>
            <div class="bmi-bar mt-1 opacity-80">
              {#each fmiCategories as category, index (index)}
                <div
                  class="category"
                  style="background-color: {category.color}; flex-grow: {category.range[1] -
                    category.range[0]};"
                ></div>
              {/each}
            </div>
            <div class="bmi-marker" style="--marker-position: {fmiMarkerPosition}%">⬤</div>
          </div>
        {/if}

        <!-- SMM Visualization Component -->
        {#if smm}
          <div class="bmi-container mx-0 my-2 text-s">
            <p>
              Skeletal Muscle Mass:
              {smm} kg ({selectedWeight?.biaCalculations?.smmp.value} %) - {currentFMICategory.name}
            </p>
            <div class="bmi-bar mt-1 opacity-80">
              {#each smmCategories as category, index (index)}
                <div
                  class="category"
                  style="background-color: {category.color}; flex-grow: {category.range[1] -
                    category.range[0]};"
                ></div>
              {/each}
            </div>
            <div class="bmi-marker" style="--marker-position: {smmMarkerPosition}%">⬤</div>
          </div>
        {/if}

        <!-- TBW Visualization Component -->
        {#if tbw}
          <div class="bmi-container mx-0 my-2 text-s">
            <p>
              Total Body Water:
              {tbw} kg ({selectedWeight?.biaCalculations?.tbwp.value} %)
            </p>
            <div class="bmi-bar mt-1 opacity-80">
              {#each tbwCategories as category, index (index)}
                <div
                  class="category"
                  style="background-color: {category.color}; flex-grow: {category.range[1] -
                    category.range[0]};"
                ></div>
              {/each}
            </div>
            <div class="bmi-marker" style="--marker-position: {tbwMarkerPosition}%">⬤</div>
          </div>
        {/if}

        <!-- ECW Visualization Component -->
        {#if ecw}
          <div class="bmi-container mx-0 my-2 text-s">
            <p>
              Extracellular Water:
              {ecw} kg ({selectedWeight?.biaCalculations?.ecwp.value} %)
            </p>
            <div class="bmi-bar mt-1 opacity-80">
              {#each ecwCategories as category, index (index)}
                <div
                  class="category"
                  style="background-color: {category.color}; flex-grow: {category.range[1] -
                    category.range[0]};"
                ></div>
              {/each}
            </div>
            <div class="bmi-marker" style="--marker-position: {ecwMarkerPosition}%">⬤</div>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  /* Styles for the BMI Visualization */
  .bmi-container {
    width: 100%;
    height: 60px;
    margin: 0 auto;
  }

  .bmi-bar {
    display: flex;
    height: 10px;
    border-radius: 5px;
    overflow: hidden;
  }

  .category {
    height: 100%;
  }

  .bmi-marker {
    position: relative;
    top: -12px;
    left: calc(var(--marker-position) - 10px); /* Adjust the marker positioning */
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: white;
    border: 2px solid white;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 12px;
    text-align: center;
  }
</style>
